import type { ComponentProps } from 'react'
import { useMemo, useContext, useRef, forwardRef, useImperativeHandle } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useCruiseDurationLabels from 'hooks/Cruise/useCruiseDurationLabels'
import { sortBy, groupBy } from 'lib/array/arrayUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import moment from 'moment'
import { INTL_DMY_CASUAL_FORMAT, INTL_SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { CruiseDurationOptions } from './CruiseWhenSelectHowLong'
import type { CruiseWhenSelectDesktopRef } from './CruiseWhenSelectDesktop'
import CruiseWhenSelectDesktop from './CruiseWhenSelectDesktop'
import CruiseWhenSelectMobile from './CruiseWhenSelectMobile'
import type DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { CruiseSearchInputProvider } from '../CruiseSearchInputContext'
const CruiseWhenWrapper = styled.div`
  overflow: hidden;
`

export interface CruiseWhenSelectRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef?: React.RefObject<HTMLElement | null>
  drawerMode?: boolean
  isChipMode?: boolean
  calendarSize?: 'S' | 'M'
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  initialFilters?: App.CruiseInitialFilters
}

const CruiseWhenSelectInner = forwardRef<CruiseWhenSelectRef, Props>(({
  drawerMode,
  onChange,
  dropdownAnchorRef,
  isChipMode,
  calendarSize,
  dropdownSize,
  initialFilters,
}, ref) => {
  const intl = useIntl()
  const durationLabels = useCruiseDurationLabels()
  const inputRef = useRef<HTMLDivElement>(null)
  const desktopInputRef = useRef<CruiseWhenSelectDesktopRef>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      desktopInputRef.current?.focus()
    },
  }), [])
  const { flexibleMonths, checkinDate, checkoutDate, durationRange } = useContext(GlobalSearchStateContext)

  const monthsLabel = useMemo(() => {
    const departureMonths = (flexibleMonths ?? '').split(',')
    const departureMonthDates = sortBy(
      departureMonths.filter(Boolean)
        .map((monthItem) => {
          const [year, month] = monthItem.split('-')
          return moment(`${year}-${month}-01`)
        }),
      (date) => date.valueOf(),
      'asc',
    )

    const departureMonthsByYear = groupBy(departureMonthDates, (date) => date.format('YYYY'))

    return Array.from(departureMonthsByYear.values())
      .map((months) => {
        const [first, ...rest] = months
        const firstMonth = intlDateFormatter(first, INTL_SHORT_MONTH_LONG_YEAR, intl.locale)
        return rest.length ? `${firstMonth} +${rest.length}` : firstMonth
      }).join(', ')
  }, [flexibleMonths, intl.locale])

  const inputValue = useMemo(() => {
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    const [firstDuration, ...durations] = CruiseDurationOptions.filter(opt => durationRange.includes(opt.duration))
    const valueDuration = firstDuration ?
      `${durationLabels[firstDuration.value] ?? firstDuration.value}${durations.length ? ` +${durations.length}` : ''}` :
      null
    const dateRangeValues = !!checkinDate && !!checkoutDate && `${intlDateFormatter(checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale)} - ${intlDateFormatter(checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale)}`

    return [valueDuration, dateRangeValues || monthsLabel].filter(Boolean).join(` ${sep} `)
  }, [checkinDate, checkoutDate, durationRange, monthsLabel, intl, durationLabels])

  return <CruiseWhenWrapper ref={inputRef}>
    <CSSBreakpoint max="mobile" dynamic>
      <CruiseWhenSelectMobile
        onChange={onChange}
        inputValue={inputValue}
        isChipMode={isChipMode}
        initialFilters={initialFilters}
      />
    </CSSBreakpoint>

    <CSSBreakpoint min="tablet" dynamic>
      {drawerMode && <CruiseWhenSelectMobile
        onChange={onChange}
        inputValue={inputValue}
        isChipMode={isChipMode}
        initialFilters={initialFilters}
      />}

      {!drawerMode && <CruiseWhenSelectDesktop
        ref={desktopInputRef}
        onChange={onChange}
        inputValue={inputValue}
        dropdownAnchorRef={dropdownAnchorRef || inputRef}
        isChipMode={isChipMode}
        calendarSize={calendarSize}
        dropdownSize={dropdownSize}
        initialFilters={initialFilters}
      />}
    </CSSBreakpoint>
  </CruiseWhenWrapper>
})

CruiseWhenSelectInner.displayName = 'CruiseWhenSelectInner'

const CruiseWhenSelect = forwardRef<CruiseWhenSelectRef, Props>((props, ref) => (
  <CruiseSearchInputProvider>
    <CruiseWhenSelectInner {...props} ref={ref} />
  </CruiseSearchInputProvider>
))

CruiseWhenSelect.displayName = 'CruiseWhenSelect'

export default CruiseWhenSelect
